import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  MapPin, Clock, Users, Dumbbell, ArrowRight, Check, Navigation2, CalendarCheck, Star,
} from 'lucide-react';
import { TopBar, Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { PageMeta, BreadcrumbJsonLd, FAQJsonLd } from '@/components/PageMeta';
import { PageHero } from '@/components/PageHero';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { programmeData } from '@/data/programmes';
import { nearbyAreas } from '@/data/seoLocal';
import { RelatedArticles } from '@/components/RelatedArticles';
import { Helmet } from 'react-helmet-async';

const EASE = [0.22, 1, 0.36, 1];

const SITE_URL = 'https://crossfit-saintchamond.fr';

// Intro templates per program (so each combination page has unique-feeling copy).
const PROGRAM_ANGLES = {
  'crossfit-wod': {
    label: 'CrossFit WOD',
    lead: (city, distance) => `Tu cherches une salle pour faire du CrossFit WOD près de ${city} ? À seulement ${distance} de chez toi, CrossFit Saint-Chamond t'accueille pour des Workouts Of the Day (WOD) encadrés par des coachs certifiés.`,
    benefits: [
      'WOD différent chaque jour — jamais de routine',
      'Scaling systématique selon ton niveau',
      'Coaching inclus à chaque séance',
      'Matériel CrossFit complet (barres olympiques, rameurs, airbikes, rings...)',
      'Créneaux 6j/7, du matin au soir',
    ],
    kw: (city) => `CrossFit WOD ${city}, cours WOD près de ${city}, salle CrossFit ${city}`,
    cta: (city) => `Viens tester un WOD gratuit à 7 Rue Jean-Jacques Rousseau — accessible en quelques minutes depuis ${city}.`,
  },
  'hyrox': {
    label: 'HYROX',
    lead: (city, distance) => `Tu prépares une compétition HYROX ou tu veux découvrir ce format en pleine explosion ? CrossFit Saint-Chamond, à ${distance} de ${city}, est HYROX Training Club officiel — unique dans la Loire.`,
    benefits: [
      'Préparation HYROX spécifique (stations réelles + course)',
      '2 créneaux HYROX hebdomadaires (lundi 17h, vendredi 19h30)',
      'Équipement HYROX sur place (SkiErg, Sled, Sandbag, Wall Balls...)',
      'Coaching stratégique pour ta compétition',
      'Format Team HYROX disponible en binôme',
    ],
    kw: (city) => `HYROX ${city}, préparation HYROX ${city}, HYROX training club près de ${city}`,
    cta: (city) => `Rejoins la seule salle HYROX Training Club de la Loire — à quelques minutes de ${city}.`,
  },
  'team-wod': {
    label: 'Team WOD',
    lead: (city, distance) => `Envie de t'entraîner en équipe ? Depuis ${city} (${distance}), viens partager un Team WOD à CrossFit Saint-Chamond — le format collectif qui soude les équipes et dépasse les limites.`,
    benefits: [
      'Entraînement en binôme ou trio',
      'Parfait pour venir entre amis ou collègues',
      'Séances du mercredi et samedi matin',
      'Ambiance motivante et compétitive',
      'Adapté à tous les niveaux (scaling par équipier)',
    ],
    kw: (city) => `Team WOD ${city}, entraînement collectif ${city}, CrossFit équipe près de ${city}`,
    cta: (city) => `Constitue ta team et viens tester le Team WOD — à seulement ${distance} de ${city}.`,
  },
  'team-hyrox': {
    label: 'Team HYROX',
    lead: (city, distance) => `Tu veux t'inscrire à une HYROX Doubles avec un partenaire ? À ${distance} de ${city}, CrossFit Saint-Chamond te prépare au format HYROX Team — courses et stations en binôme alterné.`,
    benefits: [
      'Préparation spécifique HYROX Doubles',
      'Stratégie de relais optimisée',
      'Synchronisation avec ton partenaire',
      'Créneaux dédiés mercredi et vendredi',
      'Entraînements sur stations HYROX officielles',
    ],
    kw: (city) => `HYROX Team ${city}, HYROX Doubles ${city}, préparation compétition duo près de ${city}`,
    cta: (city) => `Prépare ta HYROX Doubles avec nous — à quelques minutes seulement depuis ${city}.`,
  },
  'free-access': {
    label: 'Free Access',
    lead: (city, distance) => `Tu veux t'entraîner à ton rythme, à l'horaire qui t'arrange ? Depuis ${city} (${distance}), profite du Free Access de CrossFit Saint-Chamond : accès libre à la box sur les créneaux ouverts.`,
    benefits: [
      'Accès libre à la box sur créneaux ouverts',
      'Utilise tout le matériel sans coach',
      'Idéal pour compléter tes séances coachées',
      'Inclus dans les abonnements 3x/semaine et Illimité',
      'Unique dans la région — rare en salle CrossFit',
    ],
    kw: (city) => `Salle CrossFit accès libre ${city}, Free Access ${city}, musculation fonctionnelle ${city}`,
    cta: (city) => `Découvre le Free Access — profite d'une vraie salle CrossFit à la carte, à portée de ${city}.`,
  },
};

export default function ProgramCityPage() {
  const { programSlug, citySlug } = useParams();
  const programme = programmeData[programSlug];
  const area = nearbyAreas.find((a) => a.slug === citySlug);
  const angle = PROGRAM_ANGLES[programSlug];

  if (!programme || !area || !angle) return <Navigate to="/programmes" replace />;

  // Saint-Chamond doesn't need a "près de" page
  if (area.slug === 'saint-chamond') return <Navigate to={`/programmes/${programSlug}`} replace />;

  const title = `${angle.label} près de ${area.city} — CrossFit Saint-Chamond`;
  const description = `${angle.label} à ${area.distance} de ${area.city}. ${programme.intro} Coachs certifiés, 7 Rue Jean-Jacques Rousseau.`;
  const canonicalPath = `/cours/${programSlug}/${citySlug}`;

  const faqs = [
    {
      q: `Y a-t-il un cours de ${angle.label} à ${area.city} ?`,
      a: `Il n'y a pas de box CrossFit à ${area.city}. CrossFit Saint-Chamond, à ${area.distance} de ${area.city}, propose du ${angle.label} encadré par des coachs certifiés.`,
    },
    {
      q: `Combien de temps de trajet depuis ${area.city} ?`,
      a: `${area.distance} en voiture, soit moins de 15 minutes. Parking gratuit sur place au 7 Rue Jean-Jacques Rousseau, 42400 Saint-Chamond.`,
    },
    {
      q: `Quels sont les horaires du ${angle.label} ?`,
      a: `Horaires : ${programme.frequency}. Consulte le planning complet en ligne pour les créneaux exacts de la semaine.`,
    },
    {
      q: `Peut-on essayer gratuitement ?`,
      a: `Oui, ton premier cours est 100 % gratuit et sans engagement. Contacte-nous pour réserver ton créneau.`,
    },
  ];

  // Same program, other nearby cities — cross-linking for SEO
  const otherCities = nearbyAreas
    .filter((a) => a.slug !== citySlug && a.slug !== 'saint-chamond')
    .slice(0, 6);

  // Other programs for this city
  const otherPrograms = Object.keys(PROGRAM_ANGLES).filter((p) => p !== programSlug);

  // Hybrid schema: Course + LocalBusiness
  const courseSchema = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": `${angle.label} près de ${area.city}`,
    "description": description,
    "provider": {
      "@type": "Organization",
      "name": "CrossFit Saint-Chamond",
      "url": SITE_URL,
      "sameAs": `${SITE_URL}/programmes/${programSlug}`,
    },
    "offers": {
      "@type": "Offer",
      "availability": "https://schema.org/InStock",
      "price": "59",
      "priceCurrency": "EUR",
    },
    "hasCourseInstance": {
      "@type": "CourseInstance",
      "courseMode": "OnSite",
      "location": {
        "@type": "Place",
        "name": "CrossFit Saint-Chamond",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "7 Rue Jean-Jacques Rousseau",
          "addressLocality": "Saint-Chamond",
          "postalCode": "42400",
          "addressRegion": "Loire",
          "addressCountry": "FR",
        },
      },
    },
  };

  return (
    <div className="min-h-screen">
      <PageMeta title={title} description={description} path={canonicalPath} image={programme.heroImage} />
      <BreadcrumbJsonLd
        items={[
          { name: 'Accueil', url: '/' },
          { name: 'Programmes', url: '/programmes' },
          { name: programme.title, url: `/programmes/${programSlug}` },
          { name: `près de ${area.city}` },
        ]}
      />
      <FAQJsonLd faqs={faqs} />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(courseSchema)}</script>
      </Helmet>

      <TopBar />
      <Navigation />

      <PageHero
        eyebrow={`${angle.label} · ${area.distance} de ${area.city}`}
        title={`${angle.label.toUpperCase()} près de ${area.city}`}
        highlight={area.city}
        subtitle={angle.lead(area.city, area.distance)}
        image={programme.heroImage}
        alt={`${angle.label} près de ${area.city}`}
        breadcrumbs={[
          { name: 'Accueil', href: '/' },
          { name: 'Programmes', href: '/programmes' },
          { name: programme.title, href: `/programmes/${programSlug}` },
          { name: area.city },
        ]}
        minHeight="min-h-[55vh]"
      >
        <div className="flex flex-wrap gap-3">
          <Link to="/contact" data-testid="pc-essai-cta">
            <Button className="bg-primary hover:bg-primary-dark text-white font-heading tracking-wide px-7 py-6 rounded-lg">
              ESSAI GRATUIT
            </Button>
          </Link>
          <Link to="/reservation" data-testid="pc-reserver-cta">
            <Button
              variant="outline"
              className="border-white/30 text-white hover:bg-white/10 hover:text-white font-heading tracking-wide px-7 py-6 rounded-lg bg-transparent"
            >
              RÉSERVER UN COURS
            </Button>
          </Link>
        </div>
      </PageHero>

      {/* Main content */}
      <section className="section-padding bg-white" data-testid="pc-content">
        <div className="container-custom max-w-5xl grid lg:grid-cols-3 gap-10">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: EASE }}
            className="lg:col-span-2"
          >
            <span className="text-primary font-semibold text-xs tracking-[0.3em] uppercase">━━ {angle.label}</span>
            <h2 className="font-heading text-3xl md:text-4xl text-dark mt-3 mb-5">
              Pratiquer le {angle.label} à <span className="text-gradient-primary italic font-display">{area.distance}</span> de {area.city}
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-5">{programme.intro}</p>
            {programme.description?.[0] && (
              <p className="text-muted-foreground leading-relaxed mb-5">{programme.description[0]}</p>
            )}

            <h3 className="font-heading text-xl text-dark mt-8 mb-4">Pourquoi choisir CrossFit Saint-Chamond pour ton {angle.label}</h3>
            <div className="grid sm:grid-cols-2 gap-3 mb-8">
              {angle.benefits.map((b, i) => (
                <div key={i} className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-primary mt-1 shrink-0" />
                  <span className="text-sm text-foreground">{b}</span>
                </div>
              ))}
            </div>

            <h3 className="font-heading text-xl text-dark mt-8 mb-4">Comment venir depuis {area.city}</h3>
            <p className="text-muted-foreground leading-relaxed">
              Depuis {area.city} ({area.code}), notre box est à {area.distance} en voiture, soit environ 10 à 15 minutes de trajet
              selon l'heure. Parking gratuit sur place. Adresse : 7 Rue Jean-Jacques Rousseau, 42400 Saint-Chamond.
            </p>
          </motion.div>

          {/* Sidebar */}
          <motion.aside
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
          >
            <Card className="p-6 sticky top-28 border-border/60">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Clock className="h-4 w-4 text-primary mt-0.5" />
                  <div>
                    <div className="font-heading text-sm text-dark tracking-wide">Durée</div>
                    <div className="text-sm text-muted-foreground">{programme.duration}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Users className="h-4 w-4 text-primary mt-0.5" />
                  <div>
                    <div className="font-heading text-sm text-dark tracking-wide">Capacité</div>
                    <div className="text-sm text-muted-foreground">{programme.capacity}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Dumbbell className="h-4 w-4 text-primary mt-0.5" />
                  <div>
                    <div className="font-heading text-sm text-dark tracking-wide">Niveau</div>
                    <div className="text-sm text-muted-foreground">{programme.level}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CalendarCheck className="h-4 w-4 text-primary mt-0.5" />
                  <div>
                    <div className="font-heading text-sm text-dark tracking-wide">Horaires</div>
                    <div className="text-sm text-muted-foreground">{programme.frequency}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Navigation2 className="h-4 w-4 text-primary mt-0.5" />
                  <div>
                    <div className="font-heading text-sm text-dark tracking-wide">Distance depuis {area.city}</div>
                    <div className="text-sm text-muted-foreground">{area.distance}</div>
                  </div>
                </div>
              </div>
              <div className="h-px bg-border my-5" />
              <Link to="/contact" className="block">
                <Button className="w-full bg-primary hover:bg-primary-dark text-white font-heading tracking-wide py-5 rounded-lg">
                  ESSAI GRATUIT
                </Button>
              </Link>
            </Card>
          </motion.aside>
        </div>
      </section>

      {/* Local FAQ */}
      <section className="section-padding bg-background" data-testid="pc-faq">
        <div className="container-custom max-w-3xl">
          <h2 className="font-heading text-2xl md:text-3xl text-dark mb-8 text-center">
            Questions fréquentes — {angle.label} près de {area.city}
          </h2>
          <div className="space-y-4">
            {faqs.map((f, i) => (
              <Card key={i} className="p-5 border-border/60">
                <h3 className="font-heading text-base text-dark mb-2">{f.q}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{f.a}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Same program, other cities */}
      <section className="section-padding bg-white" data-testid="pc-other-cities">
        <div className="container-custom max-w-5xl">
          <h2 className="font-heading text-xl md:text-2xl text-dark mb-6 text-center">
            {angle.label} accessible depuis
          </h2>
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            {otherCities.map((c) => (
              <Link
                key={c.slug}
                to={`/cours/${programSlug}/${c.slug}`}
                data-testid={`pc-link-${c.slug}`}
                className="bg-background hover:bg-accent text-sm font-medium px-4 py-2 rounded-lg transition-colors"
              >
                {angle.label} près de {c.city} <span className="text-muted-foreground text-xs">({c.distance})</span>
              </Link>
            ))}
          </div>
          <div className="text-center">
            <Link to="/zones-desservies" className="text-sm text-primary hover:underline font-semibold">
              Voir toutes les zones desservies →
            </Link>
          </div>
        </div>
      </section>

      {/* Other programs same city */}
      <section className="section-padding bg-background border-t border-border" data-testid="pc-other-programs">
        <div className="container-custom max-w-5xl">
          <h2 className="font-heading text-xl md:text-2xl text-dark mb-6 text-center">
            Autres cours accessibles depuis {area.city}
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {otherPrograms.map((p) => {
              const a = PROGRAM_ANGLES[p];
              return (
                <Link key={p} to={`/cours/${p}/${citySlug}`} className="group">
                  <Card className="p-4 h-full hover:shadow-md hover:-translate-y-0.5 transition-all">
                    <div className="text-xs text-primary font-semibold tracking-wider uppercase mb-1">Près de {area.city}</div>
                    <div className="font-heading text-sm text-dark group-hover:text-primary transition-colors">
                      {a.label}
                    </div>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-dark py-16" data-testid="pc-cta">
        <div className="container-custom text-center max-w-3xl">
          <Star className="h-8 w-8 text-primary-glow mx-auto mb-4" />
          <h2 className="font-heading text-3xl md:text-4xl text-white leading-tight mb-4">
            {angle.label} près de{' '}
            <span className="text-gradient-primary italic font-display">{area.city}</span> ? <br />
            On t'attend.
          </h2>
          <p className="text-white/70 leading-relaxed mb-8">{angle.cta(area.city)}</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/contact">
              <Button className="bg-primary hover:bg-primary-dark text-white font-heading text-sm tracking-wide px-7 py-6 rounded-lg">
                ESSAI GRATUIT
              </Button>
            </Link>
            <Link to={`/programmes/${programSlug}`}>
              <Button
                variant="outline"
                className="border-white/30 text-white hover:bg-white/10 hover:text-white font-heading text-sm tracking-wide px-7 py-6 rounded-lg bg-transparent"
              >
                VOIR LE PROGRAMME
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Related articles */}
      <RelatedArticles
        keywords={[angle.label, area.city, 'CrossFit', 'Saint-Chamond']}
        title={`À lire — ${angle.label}`}
        limit={3}
      />

      <Footer />
    </div>
  );
}
